import React, { useContext, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { ContentContext } from '../../context/ContentContext';
import { gtagEnvelope } from '../../util/gtagEnvelope';
import { InputMapProps, InputMapRef } from '../../types';
import { TextArea } from '../TextArea';
import { Select } from '../Select';
import { Input } from '../Input';
import { HiddenField } from '../HiddenField';
import { ConditionalStateSelect } from '~/common/components/ConditionalStateSelect';
import { businessEmailCheck } from '~/common/util/businessEmail';
import { orderedStateList } from '~/common/util/states';
import classNames from 'classnames';
import {
  validateString,
  validateEmail,
  validatePhone,
  validateSelect,
  validateFullName,
} from '../../util/customValidation';
import { Field } from 'formik';
import style from './inputMap.module.css';
import { FORM_TYPES } from '~/common/consts';

export const InputMap = forwardRef<InputMapRef, InputMapProps>((props, ref) => {
  const { formikTouched, formikErrors, formikValues, setTextareaExistsSetState, formikRef } = props;

  const [notBusinessEmail, setNotBusinessEmail] = useState(false);
  const [checkingEmailType, setCheckingEmailType] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      isCheckingEmailOrNotBusinessEmail: () => checkingEmailType || notBusinessEmail,
    }),
    [checkingEmailType, notBusinessEmail]
  );

  const { inputs } = useContext(ContentContext);

  const trackBlur = (id: string, type?: string) => {
    const fieldId = id || type;
    gtagEnvelope('event', 'form_field_focus', {
      form_field: `form-field_${fieldId}`,
      form_field_url: `${window.location.pathname}`,
    });
  };

  const validateBusinessEmail = async (email: string) => {
    formikRef.current?.setFieldTouched('email', true);
    const invalid = validateEmail(email);
    if (invalid) {
      formikRef.current?.setFieldError('email', true);
      setNotBusinessEmail(false);
      setCheckingEmailType(false);
      return true;
    } else {
      setCheckingEmailType(true);
      const business = await businessEmailCheck(email);
      if (!business) {
        formikRef.current?.setFieldError('email', true);
        setNotBusinessEmail(true);
        setCheckingEmailType(false);
        return true;
      } else {
        formikRef.current?.setFieldError('email', false);
        setNotBusinessEmail(false);
        setCheckingEmailType(false);
        validateEmail(email);
      }
    }
    return false;
  };
  const { formType } = window.LPFormData;
  const isPartnerForm = formType === FORM_TYPES.PARTNER;
  const customValidateEmail = (value: string) => {
    if (isPartnerForm && notBusinessEmail) {
      return true;
    }
    return validateEmail(value);
  };

  const determineShowStateSelect = (countryCode: string) => {
    return (
      (isPartnerForm && countryCode === 'US') || countryCode === 'CA' || countryCode === 'AU' || countryCode === 'NZ'
    );
  };

  const [showStateSelect, setShowStateSelect] = useState(determineShowStateSelect(formikValues['country']));

  useEffect(() => {
    const changedCountry = formikValues['country'];
    setShowStateSelect(determineShowStateSelect(changedCountry));
    formikValues['state'] = showStateSelect ? orderedStateList(changedCountry)[0] : '';
  }, [formikValues['country'], showStateSelect]);

  const trackErrorBlur = (id: string, validation: (value: string | number) => boolean | string, value: string) => {
    formikRef.current?.setFieldError(id, false);
    formikRef.current?.setFieldTouched(id, true);
    if (validation(value)) {
      formikRef.current?.setFieldError(id, true);
    }
    return false;
  };

  const wrapperClasses = classNames({
    [style.inputGrid]: !isPartnerForm,
    [style.inputGridPartner]: isPartnerForm,
  });

  return (
    <div className={wrapperClasses}>
      {inputs!.map((input: any, index: number) => {
        const options: any = input.options;
        const type: string = input.type;
        const label: string = input.label;
        const hintMessageText: string = input.placeHolder;
        const id: string = input.id;
        const placeholder: string = input.placeHolder;
        const errorMessageText: string = input.error;
        const required: boolean = input.required;
        let Component = null;
        switch (type) {
          case 'select':
            Component = (
              <>
                <Field
                  name={id}
                  as={Select}
                  disabled={false}
                  error={formikErrors[id] && formikTouched[id]}
                  valid={formikValues[id] !== ''}
                  errormessage={errorMessageText}
                  validate={validateSelect}
                  required
                  key={index}
                  label={label}
                  defaultValue={formikValues[id]}
                  onBlur={(event: React.FocusEvent<HTMLSelectElement>) => {
                    trackBlur(id);
                    trackErrorBlur(id, validateSelect, event.target.value);
                  }}
                >
                  {id !== 'country' && (
                    <option value="" hidden>
                      {placeholder}
                    </option>
                  )}
                  {options!.map((option: any, optionindex: number) => {
                    const countryName = Object.keys(option)[0];
                    return (
                      <option key={optionindex.toString()} value={option[countryName]}>
                        {countryName}
                      </option>
                    );
                  })}
                </Field>
                {id === 'country' && showStateSelect && (
                  <ConditionalStateSelect
                    formikValues={formikValues}
                    formikTouched={formikTouched}
                    formikErrors={formikErrors}
                    trackBlur={trackBlur}
                    trackErrorBlur={trackErrorBlur}
                  />
                )}
              </>
            );
            break;
          case 'textarea':
            Component = (
              <Field
                setTextareaExistsSetState={setTextareaExistsSetState}
                name={type}
                required={false}
                as={TextArea}
                label={label}
                key={index}
                onFocus={() => {
                  trackBlur(id, type);
                }}
              />
            );
            break;
          case 'email':
            Component = (
              <Field
                name={id}
                type={type}
                error={formikErrors[id] && formikTouched[id]}
                errormessage={
                  notBusinessEmail && isPartnerForm
                    ? hintMessageText || 'Please use a business email.'
                    : errorMessageText
                }
                maxLength={128}
                validate={customValidateEmail}
                as={Input}
                label={label}
                key={index}
                required={isPartnerForm}
                onFocus={() => {
                  trackBlur(id);
                }}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                  if (!isPartnerForm) {
                    validateEmail(event.target.value);
                    trackErrorBlur(id, validateEmail, event.target.value);
                  } else {
                    validateBusinessEmail(event.target.value);
                    trackErrorBlur(id, customValidateEmail, event.target.value);
                  }
                }}
              />
            );
            break;
          case 'tel':
            Component = (
              <Field
                name={id}
                type={type}
                error={formikErrors[id] && formikTouched[id]}
                errormessage={errorMessageText}
                validate={required ? validatePhone : ''}
                as={Input}
                label={label}
                key={index}
                maxLength="30"
                onFocus={() => {
                  trackBlur(id);
                }}
              />
            );
            break;
          case 'hidden':
            Component = <Field name={id} as={HiddenField} key={index} />;
            break;
          default:
            Component = (
              <Field
                name={id}
                type={type}
                error={formikErrors[id] && formikTouched[id]}
                errormessage={errorMessageText}
                validate={id === 'fullName' ? validateFullName : validateString}
                as={Input}
                label={label}
                key={index}
                onFocus={() => {
                  trackBlur(id);
                }}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                  trackErrorBlur(id, validateString, event.target.value);
                }}
                index
              />
            );
            break;
        }
        return Component;
      })}
    </div>
  );
});
